import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Paper,
  TextField,
  InputAdornment,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Search,
  Add,
  Collections as CollectionsIcon,
  Bookmark,
  BookmarkBorder,
  Share,
  Delete,
  Edit,
  Visibility,
  Lock,
  Public,
  People,
} from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'

interface Collection {
  id: string
  name: string
  description: string
  imageUrl: string
  adventureCount: number
  completedCount: number
  createdBy: string
  creatorName: string
  isPublic: boolean
  isSaved: boolean
  tags: string[]
  createdDate: string
  lastUpdated: string
}

// Mock collections data
const mockCollections: Collection[] = [
  {
    id: '1',
    name: 'Historic San Francisco',
    description: 'Explore the rich history and architectural wonders of San Francisco',
    imageUrl: '/assets/images/Mountain_Image.png',
    adventureCount: 12,
    completedCount: 8,
    createdBy: 'user123',
    creatorName: 'Sarah Trail',
    isPublic: true,
    isSaved: true,
    tags: ['History', 'Architecture', 'Urban'],
    createdDate: '2025-08-15',
    lastUpdated: '2025-09-25',
  },
  {
    id: '2',
    name: 'Bay Area Nature Trails',
    description: 'Beautiful natural spots and hidden parks across the Bay Area',
    imageUrl: '/assets/images/Mountain_Image.png',
    adventureCount: 15,
    completedCount: 5,
    createdBy: 'user456',
    creatorName: 'Mike Urban',
    isPublic: true,
    isSaved: false,
    tags: ['Nature', 'Hiking', 'Outdoors'],
    createdDate: '2025-07-20',
    lastUpdated: '2025-09-28',
  },
  {
    id: '3',
    name: 'Street Art Hunt',
    description: 'Discover vibrant murals and street art installations throughout the city',
    imageUrl: '/assets/images/Mountain_Image.png',
    adventureCount: 20,
    completedCount: 0,
    createdBy: 'user789',
    creatorName: 'Carlos Art',
    isPublic: true,
    isSaved: true,
    tags: ['Art', 'Culture', 'Photography'],
    createdDate: '2025-06-10',
    lastUpdated: '2025-09-20',
  },
  {
    id: '4',
    name: 'My Weekend Adventures',
    description: 'Personal collection of my favorite weekend spots',
    imageUrl: '/assets/images/Mountain_Image.png',
    adventureCount: 8,
    completedCount: 8,
    createdBy: 'currentUser',
    creatorName: 'You',
    isPublic: false,
    isSaved: true,
    tags: ['Personal', 'Favorites'],
    createdDate: '2025-09-01',
    lastUpdated: '2025-09-30',
  },
  {
    id: '5',
    name: 'Foodie Paradise',
    description: 'Culinary adventures featuring local restaurants and food markets',
    imageUrl: '/assets/images/Mountain_Image.png',
    adventureCount: 10,
    completedCount: 6,
    createdBy: 'user321',
    creatorName: 'Rachel Foodie',
    isPublic: true,
    isSaved: false,
    tags: ['Food', 'Restaurants', 'Local'],
    createdDate: '2025-08-05',
    lastUpdated: '2025-09-27',
  },
  {
    id: '6',
    name: 'Ghost Signs Collection',
    description: 'Track down faded vintage advertisements and old signage',
    imageUrl: '/assets/images/Mountain_Image.png',
    adventureCount: 6,
    completedCount: 3,
    createdBy: 'user654',
    creatorName: 'John Historical',
    isPublic: true,
    isSaved: true,
    tags: ['History', 'Photography', 'Vintage'],
    createdDate: '2025-07-15',
    lastUpdated: '2025-09-22',
  },
]

// Helper function to render visibility chip
const getVisibilityIcon = (isPublic: boolean) => {
  return isPublic ? (
    <Chip icon={<Public />} label="Public" size="small" color="success" />
  ) : (
    <Chip icon={<Lock />} label="Private" size="small" />
  )
}

export default function CollectionsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const { user } = useAuthStore()

  const filteredCollections = mockCollections.filter(
    (collection) =>
      searchQuery === '' ||
      collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      collection.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      collection.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  const myCollections = filteredCollections.filter((c) => c.createdBy === 'currentUser')
  const savedCollections = filteredCollections.filter((c) => c.isSaved && c.createdBy !== 'currentUser')
  const publicCollections = filteredCollections.filter((c) => c.isPublic && c.createdBy !== 'currentUser' && !c.isSaved)

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
            📚 Adventure Collections
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Organize and discover curated sets of adventures
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Create Collection
        </Button>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search collections by name, description, or tags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* My Collections */}
      {myCollections.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            My Collections ({myCollections.length})
          </Typography>
          <Grid container spacing={3}>
            {myCollections.map((collection) => (
              <Grid item xs={12} sm={6} md={4} key={collection.id}>
                <CollectionCard collection={collection} isOwner={true} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Saved Collections */}
      {savedCollections.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            Saved Collections ({savedCollections.length})
          </Typography>
          <Grid container spacing={3}>
            {savedCollections.map((collection) => (
              <Grid item xs={12} sm={6} md={4} key={collection.id}>
                <CollectionCard collection={collection} isOwner={false} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Discover Public Collections */}
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          Discover Collections
        </Typography>
        <Grid container spacing={3}>
          {publicCollections.map((collection) => (
            <Grid item xs={12} sm={6} md={4} key={collection.id}>
              <CollectionCard collection={collection} isOwner={false} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {filteredCollections.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CollectionsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No collections found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {searchQuery
              ? 'Try adjusting your search criteria'
              : 'Create your first collection to organize your adventures!'}
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
            Create Collection
          </Button>
        </Paper>
      )}

      {/* Create Collection Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Collection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Collections help you organize adventures into themed groups.
          </Typography>
          {/* Form fields would go here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Collection Card Component
function CollectionCard({ collection, isOwner }: { collection: Collection; isOwner: boolean }) {
  const [isSaved, setIsSaved] = useState(collection.isSaved)
  const completionPercentage = (collection.completedCount / collection.adventureCount) * 100

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <CardMedia
        component="img"
        height="160"
        image={collection.imageUrl}
        alt={collection.name}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
          {getVisibilityIcon(collection.isPublic)}
          <IconButton
            size="small"
            onClick={() => setIsSaved(!isSaved)}
            sx={{ ml: 'auto' }}
          >
            {isSaved ? <Bookmark color="primary" /> : <BookmarkBorder />}
          </IconButton>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          {collection.name}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: 2,
          }}
        >
          {collection.description}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              {collection.completedCount}/{collection.adventureCount} completed
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
          {collection.tags.slice(0, 3).map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Box>

        <Typography variant="caption" color="text.secondary">
          by {collection.creatorName}
        </Typography>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button size="small" startIcon={<Visibility />}>
          View
        </Button>
        {isOwner && (
          <>
            <Button size="small" startIcon={<Edit />}>
              Edit
            </Button>
            <Button size="small" color="error" startIcon={<Delete />}>
              Delete
            </Button>
          </>
        )}
        {!isOwner && (
          <Button size="small" startIcon={<Share />}>
            Share
          </Button>
        )}
      </CardActions>
    </Card>
  )
}
